import { useQuery } from "@tanstack/react-query";
import { Link, createFileRoute, useParams } from "@tanstack/react-router";
import { ArrowLeft } from "lucide-react";

import { Skeleton } from "@/components/ui/skeleton";

import { useApi } from "@/api";
import { PageHeader } from "@/components/manage/PageHeader";
import { SpaceMembersSection } from "@/components/manage/SpaceMembersSection";
import { StatStrip } from "@/components/manage/StatStrip";
import { useActiveOrgId } from "@/components/manage/org-context";

export const Route = createFileRoute("/manage/objectives/$id")({
  component: ObjectiveDetailPage,
});

interface SpaceSummary {
  id: string;
  name: string;
  slug: string;
  icon: string | null;
  workspace_id: string;
  member_count: number;
  updated_at: string;
}

function ObjectiveDetailPage() {
  const api = useApi();
  const orgId = useActiveOrgId();
  const { id } = useParams({ from: "/manage/objectives/$id" });

  const objective = useQuery({
    queryKey: ["admin-objectives", orgId],
    queryFn: () => api.request<SpaceSummary[]>(`/admin/orgs/${orgId}/objectives`),
    select: (rows) => rows.find((r) => r.id === id) ?? null,
  });

  if (objective.isLoading) {
    return (
      <div className="mx-auto max-w-[960px] px-10 py-10">
        <Skeleton className="h-10 w-60" />
        <Skeleton className="mt-8 h-40 w-full" />
      </div>
    );
  }

  const o = objective.data;
  if (!o) {
    return (
      <div className="mx-auto max-w-[960px] px-10 py-10">
        <Link
          to="/manage/objectives"
          className="mb-4 inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="size-3" /> All objectives
        </Link>
        <p className="text-sm text-muted-foreground">Objective not found.</p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-[960px] px-10 py-10">
      <Link
        to="/manage/objectives"
        className="mb-4 inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="size-3" /> All objectives
      </Link>

      <PageHeader
        eyebrow="Objective"
        title={o.name}
        tabTitle={[o.name, "Objectives"]}
        meta={
          <>
            <code className="rounded bg-[var(--color-surface-soft)] px-1.5 py-0.5 font-mono text-[11px]">
              /{o.slug}
            </code>
            <span>· Updated {new Date(o.updated_at).toLocaleDateString()}</span>
          </>
        }
      />

      <StatStrip
        items={[
          { label: "Members", value: o.member_count, hint: "have access" },
          {
            label: "Slug",
            value: <span className="font-mono text-base">/{o.slug}</span>,
            hint: "url segment",
          },
          { label: "Scope", value: "people", hint: "no datasets" },
        ]}
      />

      <SpaceMembersSection spaceId={id} orgId={orgId} subjectLabel="objective" />
    </div>
  );
}
